// Does this subject have a picture on file?
//
// The media route answers 404 when a person has no avatar, which is correct and is also, in a
// list of forty people, forty failed requests and forty red lines in the operator's console.
// A picture we do not have should cost nothing to not show. So `components/Identity.tsx` asks
// this module first and only points an <img> at the media route for subjects that actually
// have one; everyone else renders initials without a request ever leaving the browser.
//
// Every mark that mounts in the same tick is answered by ONE request: the ids queue up, a
// timeout flushes them per (subject_kind, kind), and the answers land in a module-level cache
// that survives navigation. An unanswerable question (network down, session gone) resolves to
// "no picture" — initials, never a broken image.
import { useEffect, useState } from 'react';

import { request, qs } from '../api/client.js';

export type PresenceSubjectKind = 'organisation' | 'person';
export type PresenceMediaKind = 'logo' | 'avatar';

const cache = new Map<string, boolean>();
const queues = new Map<string, Set<number>>();
const listeners = new Set<() => void>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const cacheKey = (subjectKind: PresenceSubjectKind, kind: PresenceMediaKind, id: number): string =>
  `${subjectKind}/${kind}/${id}`;

function announce(): void {
  for (const listener of [...listeners]) listener();
}

function flush(): void {
  flushTimer = null;
  const batches = [...queues.entries()];
  queues.clear();
  for (const [batchKey, idSet] of batches) {
    const [subjectKind, kind] = batchKey.split('/') as [PresenceSubjectKind, PresenceMediaKind];
    const ids = [...idSet];
    if (ids.length === 0) continue;
    request<{ present: number[] }>(`/media-presence${qs({ subject_kind: subjectKind, kind, ids: ids.join(',') })}`)
      .then((answer) => {
        const present = new Set(answer.present ?? []);
        for (const id of ids) cache.set(cacheKey(subjectKind, kind, id), present.has(id));
      })
      .catch(() => {
        // We could not ask. Initials are the honest fallback; a picture is never invented.
        for (const id of ids) cache.set(cacheKey(subjectKind, kind, id), false);
      })
      .finally(announce);
  }
}

function enqueue(subjectKind: PresenceSubjectKind, kind: PresenceMediaKind, id: number): void {
  const batchKey = `${subjectKind}/${kind}`;
  let queue = queues.get(batchKey);
  if (!queue) { queue = new Set(); queues.set(batchKey, queue); }
  queue.add(id);
  if (flushTimer === null) flushTimer = setTimeout(flush, 0);
}

/**
 * `true` there is a picture · `false` there is none · `undefined` nobody has asked yet.
 * Callers render initials for everything but `true`.
 */
export function useHasMedia(
  subjectKind: PresenceSubjectKind,
  kind: PresenceMediaKind,
  id: number | null | undefined,
): boolean | undefined {
  const valid = typeof id === 'number' && Number.isInteger(id) && id > 0;
  const key = valid ? cacheKey(subjectKind, kind, id as number) : '';
  const [, bump] = useState(0);

  useEffect(() => {
    if (!valid || cache.has(key)) return;
    enqueue(subjectKind, kind, id as number);
    const listener = () => bump((n) => n + 1);
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }, [key, valid, subjectKind, kind, id]);

  if (!valid) return false;
  return cache.get(key);
}

/**
 * Forget what we know about a subject's picture — after an upload, a refresh, or a delete, so
 * the next mark asks again instead of showing yesterday's answer.
 */
export function forgetMediaPresence(
  subjectKind: PresenceSubjectKind,
  kind: PresenceMediaKind,
  id?: number,
): void {
  if (typeof id === 'number') cache.delete(cacheKey(subjectKind, kind, id));
  else for (const key of [...cache.keys()]) if (key.startsWith(`${subjectKind}/${kind}/`)) cache.delete(key);
  announce();
}
